/**
 * TopBarTitleContext - lets ANY page set the app-wide topbar's title/subtitle,
 * the way Backstage's per-page <Header title subtitle/> works — but adapted for
 * our GLOBAL topbar.
 *
 * Naming: the setter hook is `useSetTopBarTitle` (reads as an action).
 * The getter hook is `useTopBarTitleValue` (reads as a value).
 *
 * Usage in any page:
 *   import { useSetTopBarTitle } from '.../layout/TopBarTitleContext';
 *   export function SettingsPage() {
 *     useSetTopBarTitle('Settings', 'Manage your preferences');
 *     return (...);
 *   }
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';

type TitleValue = { title?: string; subtitle?: string };

type TitleContextType = TitleValue & {
  setTitle: (value: TitleValue) => void;
  clearTitle: () => void;
};

const TopBarTitleContext = createContext<TitleContextType | undefined>(
  undefined,
);

export function TopBarTitleProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<TitleValue>({});

  const setTitle = useCallback((v: TitleValue) => setValue(v), []);
  const clearTitle = useCallback(() => setValue({}), []);

  const ctx = useMemo(
    () => ({ ...value, setTitle, clearTitle }),
    [value, setTitle, clearTitle],
  );

  return (
    <TopBarTitleContext.Provider value={ctx}>
      {children}
    </TopBarTitleContext.Provider>
  );
}

/** Read the current title/subtitle (used by TopBar). */
export function useTopBarTitleValue(): TitleValue {
  const ctx = useContext(TopBarTitleContext);
  return { title: ctx?.title, subtitle: ctx?.subtitle };
}

/**
 * Set the topbar title/subtitle for as long as the calling component is mounted.
 * Clears automatically on unmount. Safe no-op if the provider isn't present.
 *
 * (Named with the `use` prefix per React rules-of-hooks; reads as an action.)
 */
export function useSetTopBarTitle(title?: string, subtitle?: string) {
  const ctx = useContext(TopBarTitleContext);
  const setTitle = ctx?.setTitle;
  const clearTitle = ctx?.clearTitle;

  useEffect(() => {
    if (!setTitle || !clearTitle) return undefined;
    setTitle({ title, subtitle });
    return clearTitle;
  }, [setTitle, clearTitle, title, subtitle]);
}
